<template>
    <v-container v-if="isVisible" class="pa-0">
        <v-form v-if="!showConfirmation" ref="form" data-id="travelCardAddForm" @submit.prevent="handleSubmit">
            <form-title :text="t('travelCard.travelCardCreateTitle')" />
            <input-field
                id="name"
                v-model="formData.name"
                :max="FIELD_RULES.CARD_NAME_MAX_CHAR"
                :min="FIELD_RULES.CARD_NAME_MIN_CHAR"
                :label="t('travelCard.travelCardNameField')"
                :required="true"
                :disabled="isSubmitting"
                validate-on="submit"
                @input="validateOnInput"
            />
            <input-field
                id="number"
                v-model="formData.number"
                type="number"
                :max="FIELD_RULES.CARD_NUMBER_LENGTH_MAX"
                :min="FIELD_RULES.CARD_NUMBER_LENGTH_MIN"
                :pattern="FIELD_PATTERNS.TRAVEL_CARD(FIELD_RULES.CARD_NUMBER_LENGTH_MIN, FIELD_RULES.CARD_NUMBER_LENGTH_MAX)"
                :pattern-error="t('validation.patternRequired')"
                class="mt-2 mb-2"
                :label="t('travelCard.travelCardNumberField')"
                :required="true"
                :disabled="isSubmitting"
                validate-on="submit"
                @keydown="preventNonNumeric"
                @input="validateOnInput"
            />
            <v-card-actions class="d-flex justify-end ma-0 pa-0">
                <card-button
                    class="flex-1-0-0"
                    size="large"
                    :inactive-label="t('form.cancel')"
                    data-id="travelCardAddFormCancelButton"
                    @click="handleCancel"
                >
                    {{ t('form.cancel') }}
                </card-button>
                <card-button
                    type="submit"
                    class="flex-1-0-0"
                    size="large"
                    variant="flat"
                    :disabled="!isFormValid"
                    :inactive-label="t('travelCard.submitButton')"
                    :loading="isSubmitting"
                    data-id="travelCardAddFormSubmitButton"
                >
                    {{ t('travelCard.submitButton') }}
                </card-button>
            </v-card-actions>
        </v-form>
    </v-container>
    <notification-bar
        v-model:show="notificationBarState.show"
        :color="notificationBarState.color"
        :icon="notificationBarState.icon"
        :border="notificationBarState.border"
        :message="notificationBarState.message"
    />
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { useCardStore } from '../../../store/cardStore';
import { CreateTravelCardDto, IFormProps } from '../../../types';
import { createTravelCard, validateTravelCardNumber } from '../../../services/api';
import CardButton from '@/features/common/components/CardButton.vue';
import { InputField, FormTitle, NotificationBar } from '@/features/common/components/form';
import { FIELD_RULES } from '../../../constants';
import { FIELD_PATTERNS } from '@/features/common/components/form/constants';
import { useFormHandling, useNotification, useFormData } from '@/features/common/components/form/composables';

const props = defineProps<IFormProps>();
const emit = defineEmits(['update:isVisible', 'submit-success', 'cancel']);

const { t } = useI18n();

const initialFormState: CreateTravelCardDto = {
    name: '',
    number: ''
};

const {
    form,
    isSubmitting,
    showConfirmation,
    isFormValid,
    toggleConfirmation,
    toggleIsSubmitting,
    validateOnInput,
    preventNonNumeric,
    resetFormValidation
} = useFormHandling();
const { notificationBarState, notificationSuccess, notificationError } = useNotification();
const { formData, resetFormData } = useFormData(initialFormState);
const { prependItem } = useCardStore();

const validateCard = async (number: string): Promise<boolean> => {
    try {
        const isValid = await validateTravelCardNumber(number);
        if (!isValid) {
            notificationBarState.value = notificationError(t('travelCard.travelCardNumberValidationFailed'));
            return false;
        }
        return true;
    } catch (error: any) {
        notificationBarState.value = notificationError(t('travelCard.travelCardNumberValidationFailed') || error?.message);
        return false;
    }
};

const saveData = async (data: CreateTravelCardDto): Promise<boolean> => {
    const isValid = await validateCard(data.number);
    if (!isValid) return false;

    try {
        const { data: newCard } = await createTravelCard(data.name, data.number);
        prependItem({ ...newCard, links: [] });
        notificationBarState.value = notificationSuccess(t('travelCard.travelCardAddSuccess'));
        return true;
    } catch (error: any) {
        notificationBarState.value = notificationError(t('travelCard.travelCardAddFailed') || error?.message);
        return false;
    }
};

const submitForm = async () => {
    try {
        toggleConfirmation(false);
        toggleIsSubmitting(true);
        const result = await saveData(formData.value);
        if (result) {
            resetFormData(form);
            emit('submit-success');
            emit('update:isVisible', false);
        }
    } finally {
        toggleIsSubmitting(false);
    }
};

const handleSubmit = async () => {
    if (!isFormValid) return;
    props.isReCheckNeeded ? toggleConfirmation(true) : await submitForm();
};

const handleCancel = () => {
    resetFormData(form);
    resetFormValidation();
    toggleConfirmation(false);
    emit('cancel');
    emit('update:isVisible', false);
};
</script>
